import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { diagnosticsClient } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Bar,
	BarChart,
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export const Route = createFileRoute("/diagnostics/analytics")({
	component: DiagnosticsAnalyticsPage,
});

const PERIOD_OPTIONS = [
	{ days: 7, labelKey: "analytics_period_7d" },
	{ days: 30, labelKey: "analytics_period_30d" },
	{ days: 90, labelKey: "analytics_period_90d" },
] as const;

const tooltipStyle = {
	borderRadius: "6px",
	border: "1px solid hsl(var(--border))",
	backgroundColor: "hsl(var(--popover))",
	color: "hsl(var(--popover-foreground))",
	fontSize: 11,
	padding: "6px 8px",
};

function formatDuration(ms: number): string {
	if (ms < 60_000) return `${Math.round(ms / 1000)}с`;
	if (ms < 3_600_000) return `${Math.round(ms / 60_000)}м`;
	const h = Math.floor(ms / 3_600_000);
	const m = Math.round((ms % 3_600_000) / 60_000);
	return `${h}ч ${m}м`;
}

function formatHour(ts: number): string {
	const d = new Date(ts * 1000);
	return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(ts: number): string {
	const d = new Date(ts * 1000);
	return d.toLocaleDateString("ru-RU", {
		day: "numeric",
		month: "short",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function DiagnosticsAnalyticsPage() {
	const { t } = useTranslation("diagnostics");
	useBreadcrumbs([{ label: t("title"), href: "/diagnostics" }, { label: t("sidebar_analytics") }]);
	const [days, setDays] = useState<number>(7);

	const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

	const timeInAppQuery = useQuery({
		queryKey: ["diagnostics", "time-in-app", today, days],
		queryFn: () =>
			diagnosticsClient.getTimeInApp({
				date: today,
				employeeId: "",
				days,
			}),
	});

	const timelineQuery = useQuery({
		queryKey: ["diagnostics", "activity-timeline", today, days],
		queryFn: () =>
			diagnosticsClient.getActivityTimeline({
				date: today,
				employeeId: "",
				days,
			}),
	});

	const appData = useMemo(() => {
		return (timeInAppQuery.data?.entries ?? []).map((e) => ({
			name: e.appName || "\u2014",
			duration: Number(e.durationMs),
			segments: Number(e.segmentCount),
		}));
	}, [timeInAppQuery.data]);

	const timelineData = useMemo(() => {
		return (timelineQuery.data?.entries ?? []).map((e) => ({
			hour: e.hour ? Number(e.hour.seconds) : 0,
			duration: Number(e.durationMs),
			segments: Number(e.segmentCount),
			apps: e.uniqueApps,
		}));
	}, [timelineQuery.data]);

	const kpi = timeInAppQuery.data;
	const isLoading = timeInAppQuery.isLoading || timelineQuery.isLoading;

	// ── Status bar ─────────────────────────────────────────────────
	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "active-time",
				content: t("analytics_status_active", {
					value: formatDuration(Number(kpi?.totalDurationMs ?? 0)),
				}),
				align: "left" as const,
			},
		],
		[t, kpi?.totalDurationMs],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				{PERIOD_OPTIONS.map((p) => (
					<Button
						key={p.days}
						variant={days === p.days ? "default" : "outline"}
						size="sm"
						className="h-9"
						onClick={() => setDays(p.days)}
					>
						{t(p.labelKey)}
					</Button>
				))}
			</div>
			{/* KPI strip */}
			<div className="grid grid-cols-4 gap-px bg-border border-b shrink-0">
				{isLoading ? (
					Array.from({ length: 4 }).map((_, i) => (
						<div key={`kpi-${i}`} className="bg-background px-3 py-1.5">
							<Skeleton className="h-3 w-16 mb-1" />
							<Skeleton className="h-5 w-20" />
						</div>
					))
				) : (
					<>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("analytics_kpi_active_time")}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{formatDuration(Number(kpi?.totalDurationMs ?? 0))}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("analytics_kpi_apps")}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{kpi?.totalApps ?? 0}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("analytics_kpi_employees")}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{kpi?.totalEmployees ?? 0}
							</p>
						</div>
						<div className="bg-background px-3 py-1.5">
							<p className="text-[10px] text-muted-foreground">
								{t("analytics_kpi_segments")}
							</p>
							<p className="text-sm font-semibold tabular-nums">
								{Number(kpi?.totalSegments ?? 0).toLocaleString()}
							</p>
						</div>
					</>
				)}
			</div>

			{/* Charts — 2-column grid */}
			<div className="flex-1 overflow-y-auto p-2">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
					{/* Time in App Bar Chart */}
					<Card className="p-3">
						<h3 className="text-xs font-medium mb-2">
							{t("analytics_time_in_app")}
						</h3>
						{isLoading ? (
							<Skeleton className="h-[240px] w-full" />
						) : appData.length === 0 ? (
							<div className="h-[240px] flex items-center justify-center text-xs text-muted-foreground">
								{t("analytics_no_data")}
							</div>
						) : (
							<ResponsiveContainer width="100%" height={240}>
								<BarChart data={appData} layout="vertical">
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="hsl(var(--border))"
									/>
									<XAxis
										type="number"
										tickFormatter={(v) => formatDuration(v)}
										tick={{ fontSize: 10 }}
									/>
									<YAxis
										type="category"
										dataKey="name"
										width={100}
										tick={{ fontSize: 10 }}
									/>
									<Tooltip
										contentStyle={tooltipStyle}
										formatter={(v) => [
											formatDuration(Number(v)),
											t("analytics_duration"),
										]}
									/>
									<Bar
										dataKey="duration"
										fill="hsl(var(--primary))"
										radius={[0, 3, 3, 0]}
									/>
								</BarChart>
							</ResponsiveContainer>
						)}
					</Card>

					{/* Activity Timeline Area Chart */}
					<Card className="p-3">
						<h3 className="text-xs font-medium mb-2">
							{t("analytics_activity_timeline")}
						</h3>
						{isLoading ? (
							<Skeleton className="h-[240px] w-full" />
						) : timelineData.length === 0 ? (
							<div className="h-[240px] flex items-center justify-center text-xs text-muted-foreground">
								{t("analytics_no_data")}
							</div>
						) : (
							<ResponsiveContainer width="100%" height={240}>
								<AreaChart data={timelineData}>
									<CartesianGrid
										strokeDasharray="3 3"
										stroke="hsl(var(--border))"
									/>
									<XAxis
										dataKey="hour"
										tickFormatter={
											days <= 1 ? formatHour : formatDate
										}
										tick={{ fontSize: 10 }}
									/>
									<YAxis
										tickFormatter={(v) => formatDuration(v)}
										tick={{ fontSize: 10 }}
									/>
									<Tooltip
										contentStyle={tooltipStyle}
										labelFormatter={(v) =>
											days <= 1
												? formatHour(Number(v))
												: formatDate(Number(v))
										}
										formatter={(v, name) => [
											name === "duration"
												? formatDuration(Number(v))
												: String(v),
											name === "duration"
												? t("analytics_duration")
												: t("analytics_segments"),
										]}
									/>
									<Area
										type="monotone"
										dataKey="duration"
										stroke="hsl(var(--primary))"
										fill="hsl(var(--primary) / 0.15)"
										strokeWidth={1.5}
									/>
								</AreaChart>
							</ResponsiveContainer>
						)}
					</Card>
				</div>
			</div>
		</div>
	);
}
